import { useMemo } from "react";
import type { LatLngTuple } from "leaflet";
import { useQuery } from "@tanstack/react-query";

import { referencesAjax } from "../../ajax/ReferencesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { HaulRoadDTO } from "../../DTOs/Production/HaulRoadDTO";

/**
 * The haul roads the machine map draws between the geofences.
 *
 * Note: parsed here rather than server-side. A road is only ever drawn, never matched against, so the API
 * passes the stored GeoJSON through untouched and this is the only thing that reads it.
 */
export const HAUL_ROAD_QUERY_KEY = ["references", "haulRoads"];

export type HaulRoad = {
  id: number;
  name: string;
  roadClass: string | null;
  lengthMetres: number | null;
  gradePercent: number | null;
  path: LatLngTuple[];
};

// Note: GeoJSON is [longitude, latitude] and Leaflet is [latitude, longitude]. The transposition happens
// here and nowhere else, for the same reason geofencePolygon.ts confines its own to two functions.
function parseLineString(raw: string): LatLngTuple[] {
  try {
    const parsed = JSON.parse(raw);
    const coords = parsed?.type === "LineString" ? parsed.coordinates : null;
    if (!Array.isArray(coords)) return [];

    return coords
      .filter((c: unknown): c is [number, number] =>
        Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]))
      .map(([lon, lat]) => [lat, lon] as LatLngTuple);
  } catch {
    // Note: a road that will not parse is dropped rather than breaking the map. It is decoration; losing
    // one line is a far better outcome than a blank map with the fleet on it.
    return [];
  }
}

export function useHaulRoads(): { roads: HaulRoad[]; isLoading: boolean } {
  const { data = [], isLoading } = useQuery({
    queryKey: HAUL_ROAD_QUERY_KEY,
    queryFn: async () => {
      const res = await referencesAjax.getHaulRoads();
      if (ErrorResponse.isErrorResponse(res) || !res.data) return [] as HaulRoadDTO[];
      return res.data;
    },
    // Note: five minutes, matching the geofences, NOT the half hour this first had. The query cache is
    // persisted to IndexedDB, so a long staleTime does not merely delay a refetch - it survives a reload,
    // and a road reseeded in the database stayed invisible through a hard refresh. Anything on this map
    // needs a stale window somebody would actually wait out.
    staleTime: 5 * 60 * 1000,
  });

  const roads = useMemo<HaulRoad[]>(
    () => data
      .map(r => ({
        id: r.haulRoadId,
        name: r.name,
        roadClass: r.roadClass ?? null,
        lengthMetres: r.lengthMetres ?? null,
        gradePercent: r.gradePercent ?? null,
        path: parseLineString(r.geometry ?? ""),
      }))
      .filter(r => r.path.length >= 2),
    [data]);

  return { roads, isLoading };
}
